import { FormikTouched, useFormik } from "formik";
import { useGraphQLAuth } from "hooks/useGraphQLAuth";
import { useToast } from "hooks/useToast";
import { ChangeEvent, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import * as Yup from "yup";
import { RegistrationProps } from "../RegistrationNew/useRegistrationNew";

const initInputsValues: RegistrationProps = {
  customerCpf: "",
  importData: {
    email: "",
    phone: "",
  },
  profile: {
    name: "",
    company: "",
    position: "",
    bio: "",
    isSpeaker: false,
    isVisible: false,
    displayPosition: undefined,
  },
};

const initTouchedInputsValues: FormikTouched<RegistrationProps> = {
  customerCpf: false,
  importData: false,
  profile: {
    name: false,
    company: false,
    position: false,
    bio: false,
    isSpeaker: false,
    isVisible: false,
    displayPosition: false,
  },
};

function normalizePhone(phone: string): string {
  if (!phone) return phone;
  const digits = phone.replace(/\D/g, "");
  if (digits.startsWith("55")) return `+${digits}`;
  return `+55${digits}`;
}

export const useRegistrationEdit = (
  eventId: string,
  registrationId: string
) => {
  const { setToast } = useToast();
  const { push } = useHistory();
  const { connectGraphQLApi, cancelRequest } = useGraphQLAuth();

  const [initialInputsValues, setInitialInputsValues] =
    useState(initInputsValues);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [uploadPhotoCallback, setUploadPhotoCallback] = useState<
    (() => Promise<boolean>) | null
  >(null);

  const {
    handleSubmit,
    handleChange: formikHandleChange,
    setValues: setInputsValues,
    values: inputsValues,
    touched,
    errors,
  } = useFormik({
    initialValues: initInputsValues,
    initialTouched: initTouchedInputsValues,
    validationSchema: Yup.object({
      customerCpf: Yup.string()
        .optional()
        .test(
          "cpf-format",
          "CPF deve ter formato válido",
          (value) =>
            !value || /^\d{11}$|^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(value)
        ),
      importData: Yup.object({
        email: Yup.string()
          .optional()
          .test(
            "email-format",
            "Email deve ter formato válido",
            (value) => !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
          ),
        phone: Yup.string()
          .optional()
          .test(
            "phone-format",
            "Telefone deve ter formato válido",
            (value) => !value || /^[\d\s()+-]+$/.test(value)
          ),
      }).optional(),
      profile: Yup.object({
        name: Yup.string().required("Nome é obrigatório"),
        company: Yup.string().required("Empresa é obrigatória"),
        position: Yup.string().required("Cargo é obrigatório"),
        bio: Yup.string().required("Biografia é obrigatória"),
        isSpeaker: Yup.boolean().optional(),
        isVisible: Yup.boolean().optional(),
        displayPosition: Yup.number()
          .optional()
          .nullable()
          .min(1, "Ordem deve ser maior que 0")
          .integer("Ordem deve ser um número inteiro"),
      }),
    }).test(
      "at-least-one-identifier",
      "Preencha pelo menos um: CPF, email ou telefone",
      (values) => {
        const hasCpf = !!values?.customerCpf;
        const hasEmail = !!values?.importData?.email;
        const hasPhone = !!values?.importData?.phone;
        return hasCpf || hasEmail || hasPhone;
      }
    ),
    onSubmit: async (_) => {
      // This will be called after validation passes
      await sendDataWithPhoto();
    },
  });

  /* HOOKS */
  useEffect(() => {
    fetchRegistrationData();
    return () => {
      cancelRequest();
    };
  }, [eventId, registrationId]);

  useEffect(() => {
    setInputsValues(initialInputsValues);
  }, [initialInputsValues]);

  /* EVENT HANDLERS */
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;

    if (type === "checkbox") {
      if (name.includes(".")) {
        const [parent, child] = name.split(".");
        setInputsValues((prev) => ({
          ...prev,
          [parent]: {
            ...(prev[parent as keyof RegistrationProps] as any),
            [child]: checked,
          },
        }));
      } else {
        setInputsValues((prev) => ({
          ...prev,
          [name]: checked,
        }));
      }
    } else {
      if (name.includes(".")) {
        const [parent, child] = name.split(".");
        setInputsValues((prev) => ({
          ...prev,
          [parent]: {
            ...(prev[parent as keyof RegistrationProps] as any),
            [child]: value,
          },
        }));
      } else {
        formikHandleChange(event);
      }
    }
  };

  /* API CALLS */
  const sendDataWithPhoto = async () => {
    // For edits, upload photo first (registrationId already exists), then save data
    if (uploadPhotoCallback) {
      const photoUploadSuccess = await uploadPhotoCallback();
      if (!photoUploadSuccess) {
        // Photo upload failed, don't proceed with form submission
        return;
      }
    }

    // Photo uploaded successfully or no photo selected, proceed with form submission
    await sendData();
  };

  const fetchRegistrationData = async () => {
    setIsLoading(true);
    setHasError(false);

    const query = `
      query BackofficeRegistrationById($registrationId: ID!) {
        backofficeRegistrationById(registrationId: $registrationId) {
          registrationId
          customerId
          customerCpf
          customer {
            cpf
            email
            phoneNumber
          }
          importData {
            cpf
            email
            phone
          }
          profile {
            name
            company
            position
            bio
            isSpeaker
            isVisible
            displayPosition
            photo {
              signedFileUrl
            }
          }
        }
      }
    `;

    try {
      const response = await connectGraphQLApi({
        query,
        variables: { registrationId },
        errorMessage: "Erro ao buscar dados da inscrição",
        useNewApi: true,
      });

      const registrationData = response?.data?.backofficeRegistrationById;

      if (registrationData) {
        // Se customerId existe, usa o CPF do customer (vinculado)
        // Senão, usa o customerCpf direto da registration (não vinculado)
        const cpfValue = registrationData.customerId
          ? registrationData.customer?.cpf || ""
          : registrationData.customerCpf || "";

        const formattedData: RegistrationProps = {
          customerCpf: cpfValue,
          importData: {
            cpf: registrationData.importData?.cpf || "",
            email:
              registrationData.customer?.email ||
              registrationData.importData?.email ||
              "",
            phone:
              registrationData.customer?.phoneNumber ||
              registrationData.importData?.phone ||
              "",
          },
          profile: {
            name: registrationData.profile?.name || "",
            company: registrationData.profile?.company || "",
            position: registrationData.profile?.position || "",
            bio: registrationData.profile?.bio || "",
            isSpeaker: registrationData.profile?.isSpeaker || false,
            isVisible: registrationData.profile?.isVisible || false,
            displayPosition:
              registrationData.profile?.displayPosition || undefined,
            photo: registrationData.profile?.photo || null,
          },
        };

        setInitialInputsValues(formattedData);
        setCustomerId(registrationData.customerId || null);
      }
    } catch (error: any) {
      console.error("[fetchRegistrationData] Error:", error);
      setHasError(true);
      setToast({
        type: "error",
        message: error?.message || "Erro ao carregar dados da inscrição.",
        isActive: true,
        timeToRemoveToast: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const sendData = async () => {
    setIsLoading(true);

    const mutation = `
      mutation BackofficeUpdateRegistration($registrationId: ID!, $input: BackOfficeRegistrationUpdateInput!) {
        backofficeUpdateRegistration(registrationId: $registrationId, input: $input) {
          registrationId
          profile {
            bio
            isSpeaker
            name
            company
            position
          }
        }
      }
    `;

    const profileInput: any = {
      bio: inputsValues.profile.bio,
      company: inputsValues.profile.company,
      isSpeaker: inputsValues.profile.isSpeaker,
      isVisible: inputsValues.profile.isVisible,
      name: inputsValues.profile.name,
      position: inputsValues.profile.position,
    };

    if (
      inputsValues.profile.displayPosition !== undefined &&
      inputsValues.profile.displayPosition !== null
    ) {
      profileInput.displayPosition = parseInt(
        inputsValues.profile.displayPosition.toString()
      );
    }

    const importDataInput: any = {};
    if (inputsValues.importData?.email) {
      importDataInput.email = inputsValues.importData.email;
    }
    if (inputsValues.importData?.phone) {
      importDataInput.phone = normalizePhone(inputsValues.importData.phone);
    }
    const cleanCpf = inputsValues.customerCpf
      ? inputsValues.customerCpf.replace(/\D/g, "")
      : undefined;
    if (cleanCpf) {
      importDataInput.cpf = cleanCpf;
    }

    const variables = {
      registrationId,
      input: {
        profile: profileInput,
        ...(Object.keys(importDataInput).length > 0 && {
          importData: importDataInput,
        }),
      },
    };

    try {
      const response = await connectGraphQLApi({
        query: mutation,
        variables,
        errorMessage: "Erro ao atualizar inscrição",
        useNewApi: true,
      });

      setToast({
        type: "success",
        message: "Inscrição atualizada com sucesso!",
        isActive: true,
        timeToRemoveToast: 3000,
      });

      // Update initial values to reflect the changes
      setInitialInputsValues(inputsValues);

      // Redirect back to event details with registrations tab
      push(`/events/${eventId}?tab=Inscrições`);
    } catch (error: any) {
      console.error("[sendData] Error:", error);
      setToast({
        type: "error",
        message: error?.message || "Erro ao atualizar inscrição.",
        isActive: true,
        timeToRemoveToast: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoUploaded = (photoData: any) => {
    setInputsValues((prev) => ({
      ...prev,
      profile: {
        ...prev.profile,
        photo: {
          fileId: photoData.fileId,
          fileUrl: photoData.fileUrl,
          signedFileUrl: photoData.signedUrl,
        },
      },
    }));
  };

  const handlePhotoRemoved = () => {
    setInputsValues((prev) => ({
      ...prev,
      profile: {
        ...prev.profile,
        photo: undefined,
      },
    }));
  };

  const handleSubmitWithPhoto = async (
    uploadCallback: (registrationId?: string) => Promise<boolean>
  ) => {
    // Store the upload callback and trigger form validation/submission
    setUploadPhotoCallback(() => uploadCallback);
    handleSubmit();
  };

  return {
    handleChange,
    inputsValues,
    touched,
    errors,
    handleSubmit,
    isLoading,
    hasError,
    customerId,
    fetchRegistrationData,
    handlePhotoUploaded,
    handlePhotoRemoved,
    handleSubmitWithPhoto,
  };
};
